import type { Modify } from "../utils/typeUtils";
import type { TeamID } from "./useWebExport";

export const TeamsUserRoles = {
  Owner: "owner",
  Editor: "editor",
  SeatManager: "seat_manager",
  Contributor: "contributor",
} as const;

export type TeamsUserRole = (typeof TeamsUserRoles)[keyof typeof TeamsUserRoles];

export const TeamsUserRolesDisplayNames: { [key in TeamsUserRole]: string } = {
  owner: "Owner",
  editor: "Editor",
  seat_manager: "Admin",
  contributor: "Contributor",
} as const;

export type TeamAddons = {
  nxImporter?: boolean;
  syncForbidden?: boolean;
  privateCloud?: boolean;
  immersiveSpace?: boolean;
  teamspaceManagement?: boolean;
};

export type Team = {
  id: TeamID;
  name: string;
  role: TeamsUserRole;
  isCreatedForAccount: boolean;
  isPrimary: boolean;
  addons?: TeamAddons;
};

export type AccountResponseData = {
  id: string;
  email: string;
  accountTeamID?: string;
  emailVerified: boolean;
  subscriptions: RawSubscription[];
  teamSubscription?: TeamSubscriptionInfo;
  availablePlans: AvailablePlan[];
  countryCode: string;
  name?: string;
  teams: Team[];
  flags?: Record<string, boolean>;
  ssoEnforced: boolean;
};

export type AvailablePlan = {
  tier: string;
  period: SubscriptionPeriod;
  trialDays: number;
  paddlePlanID: number;
};

export type TeamSubscriptionInfo = {
  id: string;
  name: string;
  seats?: number;
  purchaseDate?: string;
  expirationDate?: string;
};

export type Plan = { payLink?: string };

export enum UserError {
  InvalidConfirmationHash,
  Unknown,
}

export enum SubscriptionPeriod {
  Yearly = "yearly",
  Monthly = "monthly",
}

export enum SubscriptionBillingPeriod {
  Yearly = "yearly",
  Monthly = "monthly",
}

export enum SubscriptionTier {
  Pro = "pro",
  Lite = "lite",
}

export enum SubscriptionEffectiveFeatureSet {
  Pro = "pro",
}

export enum SubscriptionType {
  Gift = "gift",
  Edu = "edu",
  Team = "team",
  AppStore = "appstore",
  Paddle = "paddle",
  Chargebee = "chargebee",
}

type RawSubscription = {
  id?: number;
  teamSubscriptionId?: string; // For mapped chargebee subscriptions
  type: SubscriptionType;
  expiration: string;
  autorenewal: boolean;
  isTrial: boolean;
  subscriptionPeriod: SubscriptionPeriod;
  paddleId?: string;
  tier: SubscriptionTier;
  effectiveFeatureSet?: SubscriptionEffectiveFeatureSet;
  gracePeriodExpirationDate?: string;
  isAutoRenewEditable?: boolean;
  isOnlyCancelable?: boolean;
};

export type Subscription = Modify<
  RawSubscription,
  { expiration: Date; gracePeriodExpirationDate?: Date; teamId?: string; teamName?: string }
>;

export type AccountData = Modify<
  AccountResponseData,
  {
    teamSubscription?: never;
    subscriptions: Subscription[];
  }
> & {
  hasPaddleSubscription: boolean;
  hasChargebeeSubscription: boolean;
  hasAppStoreSubscription: boolean;
  subscriptionInGracePeriod: Subscription | undefined;
  canPurchaseSubscription: () => boolean;
  techTrialAvailable: boolean;
};
